import type { OrderedFlight } from "@b2bportal/air-booking-api";
import type { SessionInfo } from "@b2bportal/auth-api";
import { CallState, SummaryLineType } from "@b2bportal/core-types";
import {
  OfferType,
  type WalletPayment,
  WalletPaymentType,
} from "@b2bportal/hopper-wallet-api";
import {
  Payment,
  Product,
  type FulfillSuccess,
  type PaymentOpaqueValue,
  type ProductOpaqueValue,
} from "@b2bportal/purchase-api";
import type { RbcPartnerMetadata } from "@b2bportal/rbc-accounts-api";
import type { RbcPointsData } from "@b2bportal/rbc-points-api";
import {
  cartQuoteSelectors,
  FlightSelectors,
  FlightPassengerSelectors,
  SeatSelectors,
  ParentState,
  getParentState,
} from "@hopper-b2b/checkout";
import type {
  ChoosePaymentProperties,
  ChooseTravelerProperties,
  Currency,
  FiatPrice,
  Prices,
} from "@hopper-b2b/types";
import type { TripCategory } from "@overrides/types";
import {
  addPrices,
  multiplyPrices,
  tripCategoryToTripType,
} from "@overrides/utilities";
import { createSelector } from "@reduxjs/toolkit";
import dayjs from "dayjs";
import { sum } from "lodash-es";
import type { DoneInvokeEvent, State, StateValue } from "xstate";
import { getFixedPriceTotalWithProducts } from "../getFixedPriceTotalWithProducts";
import type { AirPriceLineData, FlightMachineContext } from "../types";
import { pointsToCashFromBrackets } from "@/components/shared/RewardsSelection/utils";
import { getFlightUpSellInteraction } from "@/tracking/upSell/flightUpSellInteraction";
import { getIsFixedPriceFromBasePrice } from "@/utilities/rewards/pricing";
import { getFulfilledFlightConfirmationId } from "@/utilities/tracking/fulfilledConfirmationId";

export type AirSummaryLineData = {
  type: SummaryLineType;
  price: Prices;
  productType?: Product;
};

type CheckoutState = State<FlightMachineContext>;
type CheckoutStateWithoutValue = Pick<CheckoutState, "context">;
type CheckoutStateWithAndWithoutValue =
  | CheckoutState
  | CheckoutStateWithoutValue;

export const getIsPriceBreakdownLoading = (
  state: CheckoutStateWithAndWithoutValue
) => {
  const tripPricing = state.context.common.tripPricing;
  const quoteBreakdownPricing =
    FlightSelectors.getQuoteBreakdownFlightPricing(state);

  return quoteBreakdownPricing === undefined && tripPricing === undefined;
};

export const getSelectedTrip = (state: CheckoutStateWithAndWithoutValue) =>
  state.context.flightShop.selectedTrip;

// Get pricing from fulfillment response (for confirmation page)
const getFulfillmentPricing = (state: CheckoutStateWithAndWithoutValue) => {
  const products = state.context[ParentState.cartFulfill]?.fulfilledProducts;
  const orderedFlight = products?.find(
    (p) => p.type === Product.MultiProviderFlight || p.type === Product.Flight
  )?.value as OrderedFlight;
  return orderedFlight?.pricingSummary;
};

export const getPricing = (state: CheckoutStateWithAndWithoutValue) => {
  const parentState = getParentState((state as CheckoutState).value);

  // On confirmation page, use fulfillment pricing
  if (parentState === ParentState.bookingConfirmation) {
    const fulfillmentPricing = getFulfillmentPricing(state);
    if (fulfillmentPricing?.pricingByPassenger?.length > 0) {
      return fulfillmentPricing;
    }
  }

  const shouldUseQuote = !(
    parentState === ParentState.contactInformation ||
    parentState === ParentState.passengerInformation
  );

  const tripPricing = state.context.common?.tripPricing;

  if (!shouldUseQuote) return tripPricing;

  const quoteTripPricing =
    FlightSelectors.getQuoteBreakdownFlightPricing(state);
  const validPriceQuote =
    (quoteTripPricing?.pricingByPassenger?.length || 0) > 0;
  return validPriceQuote ? quoteTripPricing : tripPricing;
};
export const getShopPricingInfo = (state: CheckoutStateWithAndWithoutValue) =>
  state.context.flightShop.shopPricingInfo;

export const getSelectedTripDetails = (
  state: CheckoutStateWithAndWithoutValue
) => state.context.flightShop.tripDetails;

export const getCurrencyFromPricingInfo = (
  state: CheckoutStateWithAndWithoutValue
) => {
  const shopPricingInfo = getShopPricingInfo(state);
  return {
    currencyCode: shopPricingInfo.fare?.[0]?.pricing.baseAmount.fiat
      .currencyCode as Currency,
    currencySymbol:
      shopPricingInfo.fare?.[0]?.pricing.baseAmount.fiat.currencySymbol,
  };
};

export const getSellAtCurrency = (state: CheckoutStateWithAndWithoutValue) => {
  const sellAt = cartQuoteSelectors.getQuoteBreakdown(state)?.sellAt;
  return sellAt
    ? {
        currencyCode: sellAt.fiat.currencyCode as Currency,
        currencySymbol: sellAt.fiat.currencySymbol,
      }
    : null;
};

export const getCurrencyDifferenceFromQuotebreakdown = (
  state: CheckoutStateWithAndWithoutValue
) => {
  const balance = cartQuoteSelectors.getQuoteBreakdown(state)?.balance;
  const sellAt = cartQuoteSelectors.getQuoteBreakdown(state)?.sellAt;
  if (balance && sellAt) {
    if (balance.fiat.currencyCode !== sellAt.fiat.currencyCode) {
      return true;
    }
  }
  return false;
};

// Helper to check if we should use quote breakdown pricing
const getShouldUseQuote = (state: CheckoutStateWithAndWithoutValue) => {
  const parentState = getParentState((state as CheckoutState).value);
  return !(
    parentState === ParentState.contactInformation ||
    parentState === ParentState.passengerInformation
  );
};

// Helper to ensure total is not negative
const ensureNonNegative = (value: number) => (value < 0 ? 0 : value);

// Helper to build Prices object from fiat price
const buildPricesFromFiat = (
  fiat: FiatPrice,
  additionalProps?: Partial<Prices>
): Prices => ({
  accountSpecific: {},
  ...additionalProps,
  fiat,
});

export const getFlightTotalInPricesWithoutCarrotCash = (
  state: CheckoutStateWithAndWithoutValue
) => {
  const selectedSeatsFiatPrice = SeatSelectors.getSeatTotalFiatPricing(state);
  const seatTotal = selectedSeatsFiatPrice?.value ?? 0;
  const voucherDiscount = getVoucherDiscount(state)?.fiat.value ?? 0;
  const promotionDiscount = getPromotionDiscount(state)?.fiat.value ?? 0;

  const shouldUseQuote = getShouldUseQuote(state);
  const productTotalPrice = getFlightBalance(state);

  // Use quote breakdown with products when available
  if (shouldUseQuote && productTotalPrice) {
    const tripTotal = getTripTotalFromQuoteBreakdownWithProducts(state);
    const priceToUse = tripTotal?.fiat ?? productTotalPrice.fiat;

    return buildPricesFromFiat(priceToUse, productTotalPrice);
  }

  // Fallback to trip pricing
  const tripPricing = getPricing(state);
  const flightTotal =
    tripPricing?.totalPricing?.total.fiat.value ||
    tripPricing?.totalPricing?.taxes.fiat.value ||
    0;

  if (flightTotal) {
    const tripTotal = getTripTotalFromTripPricingWithProducts(state);

    return {
      ...tripTotal,
      fiat: {
        ...tripTotal.fiat,
        //TODO(Danii): we need to figure out where voucher and promotion discount goes when we work on wallet
        value: ensureNonNegative(
          tripTotal.fiat.value + voucherDiscount - promotionDiscount
        ),
      },
    };
  }

  // Fallback to shop pricing
  const shopPricingInfo = getShopPricingInfo(state);
  const farePricing = shopPricingInfo?.fare?.[0]?.pricing;

  if (farePricing) {
    const fiatBaseAmount = farePricing.baseAmount.fiat.value || 0;
    const fiatTaxAmount = farePricing.taxAmount.fiat.value || 0;
    const fiatAdditionalMargin = farePricing.additionalMargin?.fiat.value ?? 0;

    const cfarFiatPrice = getCfarPrice(state)?.fiat.value ?? 0;
    const disruptionFiatPrice = getDisruptionPrice(state)?.fiat.value ?? 0;

    const fiatTotal =
      fiatBaseAmount +
      fiatTaxAmount +
      fiatAdditionalMargin +
      seatTotal +
      cfarFiatPrice +
      disruptionFiatPrice +
      voucherDiscount -
      promotionDiscount;

    return buildPricesFromFiat({
      currencyCode: farePricing.baseAmount.fiat.currencyCode,
      currencySymbol: farePricing.baseAmount.fiat.currencySymbol,
      value: ensureNonNegative(fiatTotal),
    });
  }

  return null;
};

// Helper to extract wallet payment discount from quote breakdown or wallet offer
const getWalletDiscount = (
  state: CheckoutStateWithAndWithoutValue,
  paymentTypePredicate: (paymentType: string) => boolean,
  offerTypePredicate: (offerType: OfferType) => boolean
): Prices | undefined => {
  // From quote breakdown
  if (state.context.cartQuote.quoteBreakdown) {
    const payment = state.context.cartQuote.quoteBreakdown.payments.find(
      (p) =>
        p.payment.type === Payment.Wallet &&
        paymentTypePredicate((p.payment.value as WalletPayment).paymentType)
    );

    if (!payment) return undefined;

    const discount = payment.payment.value.totalDiscountAmount;
    return discount.fiat.value === 0 ? undefined : discount;
  }

  // From wallet state (fallback)
  const { offer } = state.context[ParentState.wallet] as any;

  if (offer && offerTypePredicate(offer.offerType)) {
    const discount = {
      ...offer.amount,
      fiat: {
        ...offer.amount.fiat,
        value: Math.abs(offer.amount.fiat.value) || 0, // Wallet offers return values in negative
      },
    };
    return discount.fiat.value === 0 ? undefined : discount;
  }

  return undefined;
};

export const getVoucherDiscount = (
  state: CheckoutStateWithAndWithoutValue
): Prices =>
  getWalletDiscount(
    state,
    (paymentType) => paymentType === "Voucher",
    (offerType) =>
      offerType !== OfferType.Promotion && offerType !== OfferType.Credit
  );

export const getPromotionDiscount = (
  state: CheckoutStateWithAndWithoutValue
): Prices =>
  getWalletDiscount(
    state,
    (paymentType) => paymentType === WalletPaymentType.Promotion,
    (offerType) => offerType === OfferType.Promotion
  );

export const getCarrotCash = (
  state: CheckoutStateWithAndWithoutValue
): Prices => {
  // From quote breakdown
  if (state.context.cartQuote.quoteBreakdown) {
    const payment = state.context.cartQuote.quoteBreakdown.payments.find(
      (p) =>
        p.payment.type === Payment.Wallet &&
        (p.payment.value as WalletPayment).paymentType ===
          WalletPaymentType.Credit
    );

    if (!payment) return undefined;

    const carrotCash = payment.payment.value.totalDiscountAmount;
    return carrotCash.fiat.value === 0 ? undefined : carrotCash;
  }

  // From wallet state (fallback)
  const { creditOffer } = state.context[ParentState.wallet] as any;
  if (!creditOffer) return undefined;

  // Max display carrot cash usage should not exceed the balance.
  // ex. Balance: $100, Carrot cash: $150, should display Carrot cash of $100.
  const calculateCreditUsageValue = (): number | null => {
    const flightTotal = getFlightTotalInPricesWithoutCarrotCash(state);
    if (!flightTotal) return null;

    const totalWithCredit =
      flightTotal.fiat.value + creditOffer.amount.fiat.value;

    // If total would be negative, cap credit to balance amount
    if (totalWithCredit < 0) {
      return Math.abs(creditOffer.amount.fiat.value - totalWithCredit);
    }

    return Math.abs(creditOffer.amount.fiat.value);
  };

  const creditValue = calculateCreditUsageValue() || 0;
  if (creditValue === 0) return undefined;

  return {
    ...creditOffer.amount,
    fiat: {
      ...creditOffer.amount.fiat,
      value: creditValue,
    },
  };
};

export const getIsCreditPaymentAccounted = (
  state: CheckoutStateWithAndWithoutValue
): boolean => {
  return !!state.context.cartQuote.quoteBreakdown;
};

export const getFlightTotalInPrices = createSelector(
  getFlightTotalInPricesWithoutCarrotCash,
  getCarrotCash,
  getIsCreditPaymentAccounted,
  (flightsTotal, carrotCash, isCreditPaymentAccounted): Prices | null => {
    if (flightsTotal) {
      const newTotal =
        flightsTotal.fiat.value -
        (isCreditPaymentAccounted ? 0 : (carrotCash?.fiat.value ?? 0));
      return {
        accountSpecific: {},
        ...flightsTotal,
        fiat: {
          ...flightsTotal.fiat,
          value: newTotal < 0 ? 0 : newTotal,
        },
      };
    } else {
      return null;
    }
  }
);

export const getFlightBalance = (state: CheckoutStateWithAndWithoutValue) => {
  return state.context.cartQuote.quoteBreakdown?.balance;
};

/**
 * Get the number of fixed points from the fulfillment response for RBC grid pricing.
 * This is used on the confirmation page to display the correct base fare for fixed price flights.
 */
const getNumberOfFixedPointsFromFulfillment = (
  state: CheckoutStateWithAndWithoutValue
): number | undefined => {
  const fulfilledPayments =
    state.context[ParentState.cartFulfill]?.fulfilledPayments;
  const rbcPayment = fulfilledPayments?.find(
    (p) => p.type === Payment.RbcAvionPoints
  );

  if (rbcPayment && rbcPayment.value) {
    const pointsRedemptionBreakdown =
      "pointsRedemptionBreakdown" in rbcPayment.value
        ? rbcPayment.value.pointsRedemptionBreakdown
        : undefined;
    return pointsRedemptionBreakdown?.numberOfFixedPoints;
  }

  return undefined;
};

/**
 * Get the card payment amount from the fulfillment response.
 * This is the actual amount charged to the card after rewards are applied.
 * Used on the confirmation screen price breakdown to show the correct "Amount due".
 * Returns $0 when fulfillment exists but there's no card payment (fully covered by rewards).
 */
export const getCardPaymentAmountFromFulfillment = (
  state: CheckoutStateWithAndWithoutValue
): FiatPrice | undefined => {
  const fulfilledPayments =
    state.context[ParentState.cartFulfill]?.fulfilledPayments;

  // If there's no fulfillment yet, return undefined
  if (!fulfilledPayments) {
    return undefined;
  }

  const cardPayment = fulfilledPayments.find((p) => p.type === Payment.Card);
  const tripTotal = getTripTotalFromQuoteBreakdownWithProducts(state);

  // If there's a card payment, return its amount
  if (cardPayment?.value && cardPayment.value.paymentAmount) {
    return {
      value: cardPayment.value.paymentAmount.amount,
      currencyCode: cardPayment.value.paymentAmount.currency,
      currencySymbol:
        cardPayment.value.paymentAmount.currencySymbol ||
        tripTotal?.fiat.currencySymbol,
    };
  }

  // Fallback: get currency from trip total
  if (tripTotal?.fiat) {
    return {
      value: 0,
      currencyCode: tripTotal.fiat.currencyCode,
      currencySymbol: tripTotal.fiat.currencySymbol,
    };
  }

  return undefined;
};

export const getTripPriceLineItems = (
  state: CheckoutStateWithAndWithoutValue
): AirPriceLineData[] => {
  const parentState = getParentState((state as CheckoutState).value);
  const isConfirmation = parentState === ParentState.bookingConfirmation;

  // For confirmation page, use fulfilled seats from booking response
  // Otherwise, use selected seats from seat selection state
  const selectedSeats = SeatSelectors.getSelectedSeats(state);
  const fulfilledSeats = SeatSelectors.getFulfilledSeats(state);
  const seatsToUse =
    isConfirmation && fulfilledSeats ? fulfilledSeats.seats : selectedSeats;

  const tripPricing = getPricing(state);
  const shopPricingInfo = getShopPricingInfo(state);

  // Get fixed points from fulfillment response for confirmation page
  const numberOfFixedPoints = isConfirmation
    ? getNumberOfFixedPointsFromFulfillment(state)
    : undefined;
  const rewardsAccountId =
    state.context[ParentState.awardsPayment]?.rewardsAccountId;

  if (tripPricing?.pricingByPassenger?.length > 0) {
    return tripPricing?.pricingByPassenger?.map((pricing) => {
      // Sum pricing for all seats for each passenger
      // Fulfilled seats use 'personId' (post-booking) vs 'person_id' (pre-booking)
      const seats =
        Math.round(
          seatsToUse.reduce((sum, seat) => {
            const seatPersonId =
              "personId" in seat ? seat.personId : seat.person_id;
            const seatPrice =
              typeof seat.price === "string"
                ? parseFloat(seat.price)
                : seat.price;
            return seatPersonId === pricing.person.id ? sum + seatPrice : sum;
          }, 0) * 100
        ) / 100;

      // For fixed price flights on confirmation page, populate baseAmount with fixed points
      let baseAmount = pricing.baseWithoutMargin;
      if (
        isConfirmation &&
        numberOfFixedPoints &&
        rewardsAccountId &&
        numberOfFixedPoints > 0
      ) {
        // Divide fixed points by number of passengers since it's a booking-level value
        const pointsPerPassenger = Math.round(
          numberOfFixedPoints / tripPricing.pricingByPassenger.length
        );

        // Preserve existing baseAmount structure but populate accountSpecific with fixed points
        baseAmount = {
          ...baseAmount,
          accountSpecific: {
            ...(baseAmount.accountSpecific || {}),
            [rewardsAccountId]: {
              currency: "points",
              value: pointsPerPassenger,
              currencyDescription: "Fixed Points",
            },
          },
        };
      }

      const lineItems: AirPriceLineData = {
        person: pricing.person,
        baseAmount,
        taxesAndFees: pricing.taxes,
        products: [],
        passengerType: pricing.passengerType,
        taxBreakdown: pricing.taxBreakdown,
      };

      lineItems.products = [];
      if (pricing.fixedPointsOverage) {
        lineItems.products.push({
          price: pricing.fixedPointsOverage.fiat,
          key: Product.RbcPoints,
        });
      }
      if (pricing.passengerType !== "INF") {
        if (seats) {
          lineItems.products.push({
            price: { ...pricing.baseWithoutMargin.fiat, value: seats },
            key: Product.Seats,
          });
        }
      }

      return lineItems;
    });
  } else if (shopPricingInfo.fare && shopPricingInfo.fare.length > 0) {
    const baseAmount = shopPricingInfo.fare[0].pricing.baseAmount;
    const taxesAndFees = shopPricingInfo.fare[0].pricing.taxAmount;

    const lineItems = [
      {
        person: undefined,
        baseAmount,
        taxesAndFees,
        products: [],
        passengerType: "ADT",
      },
    ];

    return lineItems;
  }
  return [];
};

export const getFlightTotalSummaryLineItem = (
  state: CheckoutStateWithAndWithoutValue
): AirSummaryLineData[] => {
  const flightTotalInPrices = getFlightTotalInPrices(state);

  const lineItems: AirSummaryLineData[] = [];

  if (flightTotalInPrices) {
    lineItems.push({
      type: SummaryLineType.TOTAL,
      price: flightTotalInPrices,
    });
  }

  return lineItems;
};

export const getFlightSearchDepartureLabel = (
  state: CheckoutStateWithAndWithoutValue
) => state.context.flightShop.departureLabel;

export const getFlightSearchReturnLabel = (
  state: CheckoutStateWithAndWithoutValue
) => state.context.flightShop.returnLabel;

export const getTripCategory = (state: CheckoutStateWithAndWithoutValue) =>
  state.context.flightSearch.tripCategory;

export const getSessionInfo = ({ context }: CheckoutStateWithAndWithoutValue) =>
  context.sessionInfo as SessionInfo;

export const getStateValue = ({ value }: { value: StateValue }) => value;

export const getClientAssets = ({
  context,
}: CheckoutStateWithAndWithoutValue) => context.clientAssets;

export const getFlightSearchDestination = ({
  context,
}: CheckoutStateWithAndWithoutValue) => context.flightSearch.destination;

export const getFlightSearchOrigin = ({
  context,
}: CheckoutStateWithAndWithoutValue) => context.flightSearch.origin;

export const getDestinationCity = ({ context }: CheckoutStateWithoutValue) =>
  context.flightShop.departureLabel;

export const getOriginCity = ({ context }: CheckoutStateWithoutValue) =>
  context.flightShop.returnLabel;

export const getFlightSearchDepartureDate = ({
  context,
}: CheckoutStateWithAndWithoutValue) => context.flightSearch.departureDate;

export const getFlightSearchReturnDate = ({
  context,
}: CheckoutStateWithAndWithoutValue) => context.flightSearch.returnDate;

export const getOriginCountryCode = ({
  context,
}: CheckoutStateWithAndWithoutValue) => context.flightSearch.originCountryCode;

export const getDestinationCountryCode = ({
  context,
}: CheckoutStateWithAndWithoutValue) =>
  context.flightSearch.destinationCountryCode;

export const getChooseTravelerProperties = ({
  context,
}: CheckoutStateWithAndWithoutValue): ChooseTravelerProperties => {
  const tripCategory = getTripCategory({ context }) as TripCategory;
  const destination = getFlightSearchDestination({ context });
  const origin = getFlightSearchOrigin({ context });
  const originCountryCode = getOriginCountryCode({ context });
  const destinationCountryCode = getDestinationCountryCode({ context });
  const departureDate = getFlightSearchDepartureDate({ context });
  const returnDate = getFlightSearchReturnDate({ context });
  const formattedDepartureDate = dayjs(departureDate).format("YYYY-MM-DD");
  const formattedReturnDate =
    returnDate && dayjs(returnDate).format("YYYY-MM-DD");

  return {
    departure_date: formattedDepartureDate,
    destination_country_code: destinationCountryCode,
    destination: `${destination?.id.code.regionType}/${destination?.id.code.code}`,
    origin_country_code: originCountryCode,
    origin: `${origin?.id.code.regionType}/${origin?.id.code.code}`,
    return_date: formattedReturnDate ? formattedReturnDate : undefined,
    trip_type: tripCategoryToTripType(tripCategory),
  };
};

export const getTotalSelectedPassengers = ({
  context,
}: CheckoutStateWithAndWithoutValue): number => {
  return (
    FlightPassengerSelectors.getAllSelectedPassengerIdsParent({
      context,
    }).length || 1
  );
};

/**
 * Selected passengers count excluding lap infants.
 * Used for PDA (Premium Disruption Assistance) which is not charged for lap infants.
 * CFAR uses getTotalSelectedPassengers (includes lap infants) instead.
 */
export const getSelectedPassengersCountForFintech = ({
  context,
}: CheckoutStateWithAndWithoutValue): number => {
  return (
    FlightPassengerSelectors.getSelectedPassengerIdsParent({
      context,
    }).length || 1
  );
};

export const getChoosePaymentProperties = ({
  context,
}: CheckoutStateWithAndWithoutValue): ChoosePaymentProperties => {
  const chooseTravelerProperties = getChooseTravelerProperties({ context });
  const tripPricing = getPricing({ context });
  const totalSelectedPassengers =
    FlightPassengerSelectors.getAllSelectedPassengerIdsParent({
      context,
    }).length;
  const selectedInfants =
    FlightPassengerSelectors.getSelectedLapInfantIdsParent({
      context,
    }).length;
  const userPassengers = FlightPassengerSelectors.getUserPassengersParent({
    context,
  });
  const seatedPassengerIds =
    FlightPassengerSelectors.getSelectedPassengerIdsParent({
      context,
    });
  const selectedChildren = userPassengers.filter((pax) => {
    if (!seatedPassengerIds.includes(pax.id)) return false;
    const age = dayjs().diff(dayjs(pax.dateOfBirth, "YYYY-MM-DD"), "year");
    return age > 1 && age < 2; //TODO: centralize age related logic
  }).length;
  const totalStops = sum(
    getSelectedTripDetails({ context }).slices.map((s) => s.stops)
  );

  return {
    ...chooseTravelerProperties,
    adult_price_total_usd: 0,
    base_total_usd: 0,
    seat_infant_price_total_usd: 0,
    price_per_pax_usd: 0,
    subtotal_usd: 0,
    total_price_usd: 0,
    charged_PQ_currency:
      tripPricing?.totalPricing.total.fiat.currencyCode || "USD",
    total_price: tripPricing?.totalPricing.total.fiat.value || 0,
    pax_total: totalSelectedPassengers,
    selected_pax_children: selectedChildren,
    selected_pax_lap_infants: selectedInfants,
    selected_pax_total: totalSelectedPassengers,
    total_stops: totalStops,
  };
};

export const getTripInsuranceProduct = (
  state: CheckoutStateWithAndWithoutValue
) => {
  const products = FlightSelectors.getQuoteBreakdownProducts(state);
  return products?.find((p) => p.product.type === Product.RbcInsurance);
};

export const getTripInsuranceFiatPrice = (
  state: CheckoutStateWithAndWithoutValue
): FiatPrice | undefined => {
  const insuranceProduct = getTripInsuranceProduct(state);
  if (!insuranceProduct) return undefined;

  const currency = getCurrencyFromPricingInfo(state);
  return {
    value:
      insuranceProduct.product.value.premiumAmount.value +
      insuranceProduct.product.value.taxAmount.value,
    currencyCode: currency.currencyCode,
    currencySymbol: currency.currencySymbol,
  };
};

export const getAdditionalPointsPurchaseProduct = (
  state: CheckoutStateWithAndWithoutValue
): RbcPointsData | undefined => {
  const products = FlightSelectors.getQuoteBreakdownProducts(state);
  return products?.find((p) => p.product.type === Product.RbcPoints)?.product
    .value;
};

/**
 * Gets CFAR price - either from quote breakdown or from selected offer.
 * Handles cart update scenario where CFAR is selected but not yet in breakdown.
 */
export const getCfarPrice = (
  state: CheckoutStateWithAndWithoutValue
): Prices | undefined => {
  const products = FlightSelectors.getQuoteBreakdownProducts(state);
  const cfarProduct = products?.find(
    (p) => p.product.type === Product.HtsAirCfar
  );

  // If CFAR is already in the breakdown, use that price
  if (cfarProduct) {
    return cfarProduct.product.value.totalPremiumAmount;
  }

  // Otherwise, check if CFAR is selected but not yet in breakdown (cart update pending)
  const selectedOffer = state.context[ParentState.htsAirCfar]?.selectedOffer;
  if (selectedOffer?.offer) {
    const totalPassengers = getTotalSelectedPassengers(state);
    return multiplyPrices(
      selectedOffer.offer.feeAmountPerPassenger,
      totalPassengers
    );
  }

  return undefined;
};

/**
 * Gets Disruption price - either from quote breakdown or from selected offer.
 * Handles cart update scenario where Disruption is selected but not yet in breakdown.
 */
export const getDisruptionPrice = (
  state: CheckoutStateWithAndWithoutValue
): Prices | undefined => {
  const products = FlightSelectors.getQuoteBreakdownProducts(state);
  const disruptionProduct = products?.find(
    (p) => p.product.type === Product.AirDisruption
  );

  // If Disruption is already in the breakdown, use that price
  if (disruptionProduct) {
    return disruptionProduct.product.value.quotes?.[0]?.premiumAmountLocalized;
  }

  // Otherwise, check if Disruption is selected but not yet in breakdown (cart update pending)
  const selectedOfferQuote =
    state.context[ParentState.disruptionV1]?.selectedOfferQuote;
  if (selectedOfferQuote?.quote) {
    // Use selected passengers count excluding infants for disruption pricing
    const totalPassengers = getSelectedPassengersCountForFintech(state);
    return multiplyPrices(
      selectedOfferQuote.quote.pricePerPax,
      totalPassengers
    );
  }

  return undefined;
};

/**
 * Adds attached products to a trip pricing balance.
 */
const addAttachedProductsToTripPricing = (
  tripPricing: Prices,
  state: CheckoutStateWithAndWithoutValue
): Prices => {
  const cfarPrice = getCfarPrice(state);
  const disruptionPrice = getDisruptionPrice(state);

  let pendingProductAwareTripPricing = tripPricing;

  if (cfarPrice != null) {
    pendingProductAwareTripPricing = addPrices(
      pendingProductAwareTripPricing,
      cfarPrice
    );
  }

  if (disruptionPrice != null) {
    pendingProductAwareTripPricing = addPrices(
      pendingProductAwareTripPricing,
      disruptionPrice
    );
  }

  return pendingProductAwareTripPricing;
};

/**
 * Adds pending attached products to a quoted balance.
 * Only adds products that are selected but not yet in the quote breakdown.
 * Used when cart update is pending and fintech products need to be included in totals.
 */
const addPendingAttachedProductsToQuotedBalance = (
  balance: Prices,
  state: CheckoutStateWithAndWithoutValue
): Prices => {
  const products = FlightSelectors.getQuoteBreakdownProducts(state);

  const hasCfarInBreakdown = products?.some(
    (p) => p.product.type === Product.HtsAirCfar
  );
  const hasDisruptionInBreakdown = products?.some(
    (p) => p.product.type === Product.AirDisruption
  );

  if (hasCfarInBreakdown && hasDisruptionInBreakdown) {
    return balance;
  }

  const cfarPrice = getCfarPrice(state);
  const disruptionPrice = getDisruptionPrice(state);

  const isCfarQuotePending = !hasCfarInBreakdown && cfarPrice != null;
  const isDisruptionQuotePending =
    !hasDisruptionInBreakdown && disruptionPrice != null;

  let quoteBalance = balance;

  if (isCfarQuotePending) {
    quoteBalance = addPrices(quoteBalance, cfarPrice);
  }

  if (isDisruptionQuotePending) {
    quoteBalance = addPrices(quoteBalance, disruptionPrice);
  }

  return quoteBalance;
};

/**
 * Determines if quote breakdown is for a fixed price flight.
 */
export const getIsFixedPriceQuote = (
  state: CheckoutStateWithAndWithoutValue
): boolean => {
  const pricing = FlightSelectors.getQuoteBreakdownFlightPricing(state);
  const rewardsAccountId =
    state.context[ParentState.awardsPayment]?.rewardsAccountId;

  return (
    Boolean(pricing?.totalPricing) &&
    Boolean(rewardsAccountId) &&
    getIsFixedPriceFromBasePrice(
      pricing.totalPricing.baseWithoutMargin,
      rewardsAccountId
    )
  );
};

/** Helper function to try and get the fixed rewards price from the base price. */
const tryGetFixedRewardsPrice = (
  basePrice: Prices | undefined,
  rewardsAccountId: string
) => {
  if (!basePrice) return undefined;

  const isFixedPrice = getIsFixedPriceFromBasePrice(
    basePrice,
    rewardsAccountId
  );

  if (!isFixedPrice) return undefined;

  const rewardsPrice = basePrice.accountSpecific?.[rewardsAccountId];
  return rewardsPrice?.value ? rewardsPrice : undefined;
};

/**
 * Gets the base fare rewards price from the quote breakdown taxes.
 * This is the rewards price before any products (seats, insurance, etc.) are added.
 * Used for calculating available points balance in fixed price flows.
 */
export const getBaseFareFixedRewardsPrice = (
  state: CheckoutStateWithAndWithoutValue
) => {
  const rewardsAccountId =
    state.context[ParentState.awardsPayment].rewardsAccountId;

  if (!rewardsAccountId) {
    return undefined;
  }

  // Try checkout machine pricing first (context.common is only defined in flight checkout machine)
  if (state.context.common) {
    const pricing = FlightSelectors.getPricing(state);
    const rewardsPrice = tryGetFixedRewardsPrice(
      pricing?.totalPricing?.baseWithoutMargin,
      rewardsAccountId
    );
    if (rewardsPrice) return rewardsPrice;
  }

  // Fall back to shop pricing info
  const shopPricingInfo = FlightSelectors.getShopPricingInfo(state);
  if (shopPricingInfo?.fare?.[0]) {
    return tryGetFixedRewardsPrice(
      shopPricingInfo.fare[0].pricing.baseAmount,
      rewardsAccountId
    );
  }

  return undefined;
};

/**
 * Helper to get trip total from fulfillment response (for confirmation page).
 * After fulfill, the cart context is cleared, so we need to extract pricing
 * from the fulfilled products/payments instead of the cart quote.
 *
 * For flights: Uses fulfilledProducts.pricingSummary
 * For other products (hotels, packages, etc.): Uses fulfilledPayments to calculate total
 */
const getTripTotalFromFulfillment = (
  state: CheckoutStateWithAndWithoutValue
): Prices | undefined => {
  const products = state.context[ParentState.cartFulfill]?.fulfilledProducts;

  const rewardsAccountId =
    state.context[ParentState.awardsPayment]?.rewardsAccountId;

  // Try to get pricing from flight product (flights have pricing in fulfilledProducts)
  const flightProduct = products?.find(
    (p) => p.type === Product.MultiProviderFlight || p.type === Product.Flight
  )?.value as OrderedFlight;

  if (flightProduct?.pricingSummary?.totalPricing) {
    const fulfillmentPricing = flightProduct.pricingSummary;
    const isFixedPrice =
      rewardsAccountId &&
      getIsFixedPriceFromBasePrice(
        fulfillmentPricing.totalPricing.baseWithoutMargin,
        rewardsAccountId
      );

    if (isFixedPrice) {
      // For fixed price, calculate total with products from fulfilled seats
      const fulfilledSeats = SeatSelectors.getFulfilledSeats(state);
      const currency = getCurrencyFromPricingInfo(state);

      // Calculate total seat price from fulfilled seats
      let seatTotalValue = 0;
      if (fulfilledSeats?.seats) {
        seatTotalValue = fulfilledSeats.seats.reduce((sum: number, seat) => {
          const seatPrice =
            typeof seat.price === "string"
              ? parseFloat(seat.price)
              : (seat.price ?? 0);
          return sum + seatPrice;
        }, 0);
      }

      const seatTotalFiatPrice =
        seatTotalValue > 0
          ? {
              ...currency,
              value: seatTotalValue,
            }
          : undefined;

      return getFixedPriceTotalWithProducts(
        fulfillmentPricing.totalPricing,
        seatTotalFiatPrice,
        undefined, // trip insurance
        undefined, // additional points purchase
        undefined, // cfar
        undefined // disruption
      );
    }

    // For flex price flights, return the total pricing from fulfillment
    return fulfillmentPricing.totalPricing.total;
  }

  return undefined;
};

/**
 * Calculates the total price for a fixed price flight with all products included.
 * Returns the quoted balance for flex price flights.
 * On the confirmation page, uses the fulfillment response instead of cart quote.
 */
export const getTripTotalFromQuoteBreakdownWithProducts = (
  state: CheckoutStateWithAndWithoutValue
): Prices | undefined => {
  const parentState = getParentState((state as CheckoutState).value);
  const products = state.context[ParentState.cartFulfill]?.fulfilledProducts;
  const isFlightFunnel = products?.some(
    (p) => p.type === Product.MultiProviderFlight || p.type === Product.Flight
  );

  // On confirmation page, use fulfillment pricing instead of cart quote
  // Only for flights - other funnels (hotels, cars, activities) use quote breakdown
  // Exclude packages: they include a Flight product in fulfilledProducts but lack
  // the flight shop context that getTripTotalFromFulfillment requires.
  const isPackageFunnel = products?.some((p) => p.type === Product.Package);
  if (
    parentState === ParentState.bookingConfirmation &&
    isFlightFunnel &&
    !isPackageFunnel
  ) {
    const fulfillmentTotal = getTripTotalFromFulfillment(state);
    if (fulfillmentTotal) return fulfillmentTotal;
  }

  const quotedBalance = cartQuoteSelectors.getCartQuotedBalance(state);
  if (!quotedBalance) return undefined;

  const isFixedPrice = getIsFixedPriceQuote(state);
  const pricing = FlightSelectors.getQuoteBreakdownFlightPricing(state);
  const rewardsAccountId =
    state.context[ParentState.awardsPayment].rewardsAccountId;

  if (isFixedPrice && pricing?.totalPricing && rewardsAccountId) {
    const selectedSeatsFiatPrice = SeatSelectors.getSeatTotalFiatPricing(state);
    const tripInsuranceFiatPrice = getTripInsuranceFiatPrice(state);
    const additionalPointsPurchaseProduct =
      getAdditionalPointsPurchaseProduct(state);
    const additionalPointsPurchaseFiatPrice =
      additionalPointsPurchaseProduct?.price?.fiat;
    const cfarFiatPrice = getCfarPrice(state)?.fiat;
    const disruptionFiatPrice = getDisruptionPrice(state)?.fiat;

    return getFixedPriceTotalWithProducts(
      pricing.totalPricing,
      selectedSeatsFiatPrice,
      tripInsuranceFiatPrice,
      additionalPointsPurchaseFiatPrice,
      cfarFiatPrice,
      disruptionFiatPrice
    );
  }

  // Flex pricing: balance from quote breakdown
  // Add any fintech products that are selected but not yet in breakdown (cart update pending)
  return addPendingAttachedProductsToQuotedBalance(quotedBalance, state);
};

export const getTripTotalFromTripPricingWithProducts = (
  state: CheckoutStateWithAndWithoutValue
): Prices | undefined => {
  const tripPricing = state.context.common.tripPricing;

  const rewardsAccountId =
    state.context[ParentState.awardsPayment].rewardsAccountId;

  if (!tripPricing?.totalPricing) return undefined;

  const isFixedPrice =
    rewardsAccountId != null &&
    getIsFixedPriceFromBasePrice(
      tripPricing.totalPricing.baseWithoutMargin,
      rewardsAccountId
    );

  if (isFixedPrice) {
    const selectedSeatsFiatPrice = SeatSelectors.getSeatTotalFiatPricing(state);
    // const tripInsuranceFiatPrice = getTripInsuranceFiatPrice(state);
    const additionalPointsPurchase =
      state.context[ParentState.awardsPayment].additionalPointsPurchase;
    const additionalPointsPurchaseFiatPrice =
      additionalPointsPurchase?.totalCost.fiat;
    const cfarFiatPrice = getCfarPrice(state)?.fiat;
    const disruptionFiatPrice = getDisruptionPrice(state)?.fiat;

    return getFixedPriceTotalWithProducts(
      tripPricing.totalPricing,
      selectedSeatsFiatPrice,
      undefined,
      additionalPointsPurchaseFiatPrice,
      cfarFiatPrice,
      disruptionFiatPrice
    );
  }

  return addAttachedProductsToTripPricing(
    tripPricing.totalPricing.total,
    state
  );
};

/**
 * Calculates the cash equivalent value for rewards points being applied, from
 * the backend `pointsToCash/quote` redemption brackets.
 *
 * This is the single source of truth for cash equivalent calculations across the app.
 * Used by both the RewardsSelection component and checkout state machine.
 *
 * @param state - Checkout state (flight or non-flight)
 * @param rewardsPoints - Number of rewards points to convert to cash
 * @returns Cash equivalent with proper currency, or undefined if calculation not possible
 */
export const getRewardsAmountCashEquivalent = (
  state: CheckoutStateWithAndWithoutValue,
  rewardsPoints: number
): FiatPrice | undefined => {
  const awardsPayment = state.context[ParentState.awardsPayment];
  const quoteBreakdown = state.context[ParentState.cartQuote]?.quoteBreakdown;

  // The BE brackets are the ONLY source of truth and are consumed as-is
  // (multi-rate aware). There is no multiplier fallback by design; absent
  // brackets => undefined (still loading, or the fetch failed and redemption is
  // not offered). Brackets already exclude non-redeemable products (e.g.
  // insurance) and cap at the final cumulative cutoff.
  const brackets = awardsPayment?.pointsRedemption?.brackets;
  if (!quoteBreakdown || !brackets?.length) {
    return undefined;
  }
  const { value } = pointsToCashFromBrackets(
    rewardsPoints,
    brackets,
    quoteBreakdown.balance.fiat.currencyCode
  );
  return {
    ...quoteBreakdown.balance.fiat,
    value: Math.round(value * 100) / 100,
  };
};

export const getFailedFintechProducts = (
  state: CheckoutStateWithAndWithoutValue
): Product[] => {
  const error = state.context[ParentState.cartUpdate]?.error;
  const failedProductAdds = error?.data?.failedProductAdds || [];

  return failedProductAdds
    .filter(
      (failedProductAdd) =>
        failedProductAdd.id.product === Product.HtsAirCfar ||
        failedProductAdd.id.product === Product.AirDisruption
    )
    .map((failedFintechProduct) => failedFintechProduct.id.product);
};

/**
 * Extracts additional tracking properties for complete_buy_air event.
 * Includes payment split (CAD only), USD pricing from backend, and product-specific pricing.
 */
export const getCompleteBuyAirAdditionalProperties = (
  context: FlightMachineContext,
  event?: DoneInvokeEvent<FulfillSuccess>
): Record<string, string | number | boolean | undefined> => {
  const additionalPointsPurchase =
    context[ParentState.awardsPayment]?.additionalPointsPurchase;
  const isFixedPrice = getIsFixedPriceQuote({ context });
  const insuranceProduct = getTripInsuranceProduct({ context });
  const rewardsAccountId = context[ParentState.awardsPayment]?.rewardsAccountId;

  // Account name from RBC partner metadata
  const sessionInfo = context.sessionInfo as SessionInfo;
  const partnerMetadata = sessionInfo?.userInfo
    ?.partnerMetadata as RbcPartnerMetadata;
  const accountName = partnerMetadata?.accountNameEn;

  // Grid eligibility: grid is eligible unless the fixed pricing call failed
  const is_grid_eligible =
    (context.flightShop as any)?.fixedPricingFlightsCallState !==
    CallState.Failed;

  // Get insurance policy code from product
  const insurance_policy = insuranceProduct?.product.value.productCode;

  // Get insurance pricing
  const insuranceFiatPrice = getTripInsuranceFiatPrice({ context });

  // Get OrderedFlight from event.data.products (fulfill response)
  const orderedFlight = event?.data?.products?.find(
    (p: ProductOpaqueValue) =>
      p.type === Product.MultiProviderFlight || p.type === Product.Flight
  )?.value as OrderedFlight | undefined;

  // FARE OVERAGE: Base fare exceeds the maximum allowed cap
  const fixedPointsOverage =
    orderedFlight?.pricingSummary?.totalPricing?.fixedPointsOverage;
  const over_max_ticket_price = !!fixedPointsOverage;
  const overage_amount = fixedPointsOverage?.fiat?.value;

  // ADDITIONAL POINTS PURCHASE: User doesn't have enough points to meet fixed cost
  const additional_points_purchase = !!additionalPointsPurchase;
  const additional_points_purchase_total =
    additionalPointsPurchase?.totalCost?.fiat?.value;
  const additional_points_needed =
    additionalPointsPurchase?.additionalPointsNeeded;

  // RBC price tab
  const rbc_price_tab = isFixedPrice ? "fixed" : "flex";

  const upSellInteraction = getFlightUpSellInteraction(
    context.flightShop?.selectedTrip?.tripId
  );

  // Total points amount being redeemed - get from event.data.payments
  const rbcPayment = event?.data?.payments?.find(
    (payment: PaymentOpaqueValue) => payment.type === Payment.RbcAvionPoints
  );
  const pointsBreakdown = rbcPayment?.value?.pointsRedemptionBreakdown;
  const fixedPoints = pointsBreakdown?.numberOfFixedPoints ?? 0;
  const flexiblePoints = pointsBreakdown?.numberOfFlexiblePoints ?? 0;
  const total_points_amount = fixedPoints + flexiblePoints;
  const flex_points_amount = flexiblePoints;
  const fixed_points_amount = fixedPoints;

  // Extract product-specific pricing
  const tripPricing = getPricing({ context });
  const baseFarePricing = tripPricing?.totalPricing?.baseWithoutMargin;

  // Taxes and fees
  const taxes_and_fees_cad = tripPricing?.totalPricing?.taxes?.fiat?.value;

  const selectedSeatsFiatPrice = SeatSelectors.getSeatTotalFiatPricing({
    context,
  })?.value;

  // Extract price prediction recommendation
  const recommendation = context.prediction?.recommendationV2;

  return {
    confirmation_id: getFulfilledFlightConfirmationId(event),
    accountName,
    is_grid_eligible,
    over_max_ticket_price,
    overage_amount,
    additional_points_purchase,
    additional_points_purchase_total,
    additional_points_needed,
    insurance_policy,
    rbc_price_tab,
    ...upSellInteraction,
    total_points_amount,
    flex_points_amount,
    fixed_points_amount,
    taxes_and_fees_cad,
    seats_selected_price_cad: selectedSeatsFiatPrice,
    charged_insurance_total_cad: insuranceFiatPrice?.value,
    base_total_cad: baseFarePricing?.fiat?.value,
    base_total_points:
      baseFarePricing?.accountSpecific[rewardsAccountId]?.value,
    recommendation,
    // RBC-2641: offer attribution on all complete_buy events.
    // No offers on flights yet; send 0 so a blank means a bug.
    offer_value_cad: 0,
  };
};
